import { BASE_UNIT_IDS, BASE_UNITS } from "@cloudnest/warchest-engine";
import { WC_UNIT_ABILITY, WC_UNIT_NAME } from "../i18n";
import { WarchestUnitSymbolIcon } from "./WarchestUnitSymbol";

interface WarchestUnitCardsProps {
  readonly onClose: () => void;
}

// "View Cards" reference popover (design ref → Skill cards). Read-only roster
// of every base unit: coin glyph, Korean name, coin count, and ability text.
// Dismissed via the explicit close button (matches the ActionPicker modal
// convention — no interactive backdrop).
export function WarchestUnitCards({ onClose }: WarchestUnitCardsProps) {
  return (
    <div
      className="fixed inset-0 z-20 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="유닛 카드"
        data-testid="warchest-unit-cards"
        className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl p-4"
        style={{
          background: "var(--cn-warchest-card-bg)",
          border: "1px solid var(--cn-warchest-card-border)",
        }}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2
            className="font-bold text-lg"
            style={{ color: "var(--cn-warchest-text)" }}
          >
            유닛 카드
          </h2>
          <button
            type="button"
            data-testid="warchest-unit-cards-close"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm"
            style={{
              border: "1px solid var(--cn-warchest-card-border)",
              color: "var(--cn-warchest-text)",
            }}
          >
            닫기
          </button>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {BASE_UNIT_IDS.map((id) => (
            <div
              key={id}
              data-testid={`warchest-unit-card-${id}`}
              className="flex gap-3 rounded-lg p-3"
              style={{
                background: "var(--cn-warchest-bg)",
                border: "1px solid var(--cn-warchest-card-border)",
              }}
            >
              <div
                className="flex h-10 w-10 flex-none items-center justify-center rounded-full"
                style={{
                  background: "var(--cn-warchest-coin-rim)",
                  border: "1px solid var(--cn-warchest-coin-rim-stroke)",
                }}
              >
                <WarchestUnitSymbolIcon
                  unitType={id}
                  owner="P1"
                  size={22}
                  symbolColor="var(--cn-warchest-text)"
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-semibold text-sm"
                    style={{ color: "var(--cn-warchest-text)" }}
                  >
                    {WC_UNIT_NAME[id] ?? id}
                  </span>
                  <span
                    className="font-mono text-xs"
                    style={{ color: "var(--cn-warchest-text-muted)" }}
                  >
                    ×{BASE_UNITS[id]?.coinCount ?? "?"}
                  </span>
                </div>
                <div
                  className="mt-0.5 text-xs"
                  style={{ color: "var(--cn-warchest-text-muted)" }}
                >
                  {WC_UNIT_ABILITY[id] ?? ""}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div data-testid="warchest-action-key" className="mt-4">
          <h3
            className="mb-2 font-semibold text-sm"
            style={{ color: "var(--cn-warchest-text)" }}
          >
            액션 안내
          </h3>
          <div className="flex flex-col gap-1.5">
            {[
              {
                tone: "var(--cn-warchest-raven)",
                fg: "var(--cn-warchest-card-bg)",
                label: "배치 — 보드에 앞면으로",
                actions: "배치 · 강화",
              },
              {
                tone: "var(--cn-warchest-card-border)",
                fg: "var(--cn-warchest-text)",
                label: "뒷면 — 뒷면으로 버림",
                actions: "선공권 획득 · 모집 · 패스",
              },
              {
                tone: "var(--cn-warchest-attack)",
                fg: "var(--cn-warchest-card-bg)",
                label: "기동 — 버림 더미에 앞면으로",
                actions: "이동 · 점령 · 공격 · 전술",
              },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-2 text-xs">
                <span
                  className="flex-none rounded-full px-2 py-0.5 font-semibold"
                  style={{ background: row.tone, color: row.fg }}
                >
                  {row.label}
                </span>
                <span style={{ color: "var(--cn-warchest-text-muted)" }}>
                  {row.actions}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
